import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "./hrmsNotifications";
import Apis from "../../utils/apis";
import { API_AXIOS } from "../../http/interceptor";
import { createAsyncThunk } from "@reduxjs/toolkit";

export const leadsApi = createApi({
  reducerPath: "leadList",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Leads", "LeadFolders"],
  endpoints: (builder) => ({
    getLeadsData: builder.query({
      query: ({
        page = 1,
        limit,
        type,
        status,
        individual,
        personalLeads="include",
        duplicateLeads = "all",
        search,
        source,
        pagination = true,
        srManager,
        manager,
        assistantManager,
        teamLead,
        agent,
        dateKey,
        startDate,
        endDate,
        skipType,
        projectId,
        leadpool,
        sort,
        order,
        exclude = {},
        teamId = "",
        questionFilters = [],
        folderId = "",
      }) => ({
        url: Apis.leads,
        params: {
          page,
          limit,
          type,
          status,
          individual,
          personalLeads,
          duplicateLeads,
          search,
          source,
          pagination,
          srManager,
          manager,
          assistantManager,
          teamLead,
          agent,
          startDate,
          endDate,
          dateKey,
          skipType,
          projectId,
          leadpool,
          sort,
          order,
          exclude: encodeURIComponent(JSON.stringify(exclude || {})),
          teamId,
          folderId,
          questionFilters: questionFilters.length
            ? encodeURIComponent(JSON.stringify(questionFilters))
            : undefined,
        },
      }),
      providesTags: ["Leads"],
      keepUnusedDataFor: 300,
    }),
    searchLeadOptions: builder.query({
      query: ({ search = "", page = 1, limit = 20, individual }) => ({
        url: `${Apis.leads}/search-options`,
        params: { search, page, limit, individual },
      }),
      providesTags: ["Leads"],
      keepUnusedDataFor: 60,
    }),
    getLeadQuestionFilters: builder.query({
      query: (projectId) => ({
        url: `${Apis.leads}/question-filters`,
        params: { projectId },
      }),
      keepUnusedDataFor: 300,
    }),
    getLeadFolders: builder.query({
      query: () => ({
        url: `${Apis.leads}/folders`,
      }),
      providesTags: ["LeadFolders"],
    }),
    createLeadFolder: builder.mutation({
      query: (body) => ({
        url: `${Apis.leads}/folders`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["LeadFolders"],
    }),
    updateLeadFolder: builder.mutation({
      query: ({ folderId, ...body }) => ({
        url: `${Apis.leads}/folders/${folderId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["LeadFolders"],
    }),
    deleteLeadFolder: builder.mutation({
      query: (folderId) => ({
        url: `${Apis.leads}/folders/${folderId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["LeadFolders", "Leads"],
    }),
    moveLeadsToFolder: builder.mutation({
      query: ({ folderId, leadIds }) => ({
        url: `${Apis.leads}/folders/${folderId}/leads`,
        method: "POST",
        body: { leadIds },
      }),
      invalidatesTags: ["LeadFolders", "Leads"],
    }),
    removeLeadsFromFolder: builder.mutation({
      query: ({ folderId, leadIds }) => ({
        url: `${Apis.leads}/folders/${folderId}/leads`,
        method: "DELETE",
        body: { leadIds },
      }),
      invalidatesTags: ["LeadFolders", "Leads"],
    }),
  }),
});

export const {
  useGetLeadsDataQuery,
  useSearchLeadOptionsQuery,
  useLazyGetLeadQuestionFiltersQuery,
  useGetLeadFoldersQuery,
  useCreateLeadFolderMutation,
  useUpdateLeadFolderMutation,
  useDeleteLeadFolderMutation,
  useMoveLeadsToFolderMutation,
  useRemoveLeadsFromFolderMutation,
} = leadsApi;

export const assignedHistory = createApi({
  reducerPath: "assignedHistory",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Assigned"],
  endpoints: (builder) => ({
    getAssignedHistory: builder.query({
      query: (id) => ({
        url: `${Apis.assignedHistoryById}/${id}`,
      }),
      providesTags: ["LeadDetail"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetAssignedHistoryQuery } = assignedHistory;

export const myLeads = createApi({
  reducerPath: "myLeads",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["myLeads"],
  endpoints: (builder) => ({
    getMyLeads: builder.query({
      query: ({ search }) => ({
        url: Apis.myLeads,
        params: { search },
      }),
      providesTags: ["myLeads"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetMyLeadsQuery } = myLeads;

export const statusHistory = createApi({
  reducerPath: "statusHistory",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Lead Status"],
  endpoints: (builder) => ({
    getStatusHistory: builder.query({
      query: (id) => ({
        url: `${Apis.statusHistoryById}/${id}`,
      }),
      providesTags: ["LeadDetail"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetStatusHistoryQuery } = statusHistory;

export const meetingHistory = createApi({
  reducerPath: "meetingHistory",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Meeting History"],
  endpoints: (builder) => ({
    getMeetingHistory: builder.query({
      query: (id) => ({
        url: `${Apis.meetingHistoryById}/${id}`,
      }),
      providesTags: ["MeetingHistory"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetMeetingHistoryQuery } = meetingHistory;

export const topPerformance = createApi({
  reducerPath: "topPerformance",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Top Performance"],
  endpoints: (builder) => ({
    getTopPerformance: builder.query({
      query: ({ startDate, endDate, count }) => ({
        url: `${Apis.topPerformance}`,
        params: {
          startDate,
          endDate,
          count,
        },
      }),
      providesTags: ["TopPerformance"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetTopPerformanceQuery } = topPerformance;

export const topDeveloper = createApi({
  reducerPath: "topDeveloper",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Top Developer"],
  endpoints: (builder) => ({
    getTopDeveloper: builder.query({
      query: ({ count, endDate, startDate }) => ({
        url: `${Apis.topDeveloper}`,
        params: {
          count,
          startDate,
          endDate,
        },
      }),
      providesTags: ["TopDeveloper"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetTopDeveloperQuery } = topDeveloper;

export const summaryApi = createApi({
  reducerPath: "summary",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Summary"],
  endpoints: (builder) => ({
    getSummaryOfBusiness: builder.query({
      queryFn: async ({ startDate, endDate, value }) => {
        try {
          const { data } = await API_AXIOS.post(Apis.summaryOfEOI, {
            startDate,
            endDate,
            value,
          });

          return { data: data?.data };
        } catch (error) {
          return {
            error: error.response ? error.response.data : error.message,
          };
        }
      },
      providesTags: ["Summary"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetSummaryOfBusinessQuery } = summaryApi;

export const leadQualityDashboard = createApi({
  reducerPath: "leadQuality",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Lead Quality"],
  endpoints: (builder) => ({
    leadQuality: builder.query({
      queryFn: async ({}) => {
        try {
          const { data } = await API_AXIOS.get(Apis.leadQualityDashboard);
          return { data: data ?? {} };
        } catch (error) {
          return {
            error: error.response ? error.response.data : error.message,
          };
        }
      },
      providesTags: ["Lead Quality"],
      keepUnusedDataFor: 300,
    }),
  }),
});
export const { useLeadQualityQuery } = leadQualityDashboard;

export const leadProjectWiseDashboard = createApi({
  reducerPath: "leadProjectWise",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Lead Project Wise"],
  endpoints: (builder) => ({
    leadProjectWise: builder.query({
      queryFn: async ({ startDate, endDate }) => {
        try {
          const { data } = await API_AXIOS.get(Apis.leadProjectWiseDashboard, {
            startDate,
            endDate,
          });
          return { data: data ?? {} };
        } catch (error) {
          return {
            error: error.response ? error.response.data : error.message,
          };
        }
      },
      providesTags: ["Lead Project Wise"],
      keepUnusedDataFor: 300,
    }),
  }),
});
export const { useLeadProjectWiseQuery } = leadProjectWiseDashboard;

export const closingLeadProjectWiseDashboard = createApi({
  reducerPath: "closingLeadProjectWise",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Closing Lead Project Wise"],
  endpoints: (builder) => ({
    closingLeadProjectWise: builder.query({
      queryFn: async ({ startDate, endDate }) => {
        try {
          const { data } = await API_AXIOS.get(
            Apis.closingLeadProjectWiseDashboard,
            {
              startDate,
              endDate,
            },
          );

          return { data: data ?? {} };
        } catch (error) {
          return {
            error: error.response ? error.response.data : error.message,
          };
        }
      },
      providesTags: ["Closing Lead Project Wise"],
      keepUnusedDataFor: 300,
    }),
  }),
});
export const { useClosingLeadProjectWiseQuery } =
  closingLeadProjectWiseDashboard;
